import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
// Import of the module

import { ExternalLink } from "@/components/ExternalLink";
import { formatNumber, parseDate } from "@/lib/utils";
import { Link } from "@heroui/link";
import { CheckIcon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";

// Type of the fields in the table

export type User = {
  id: number;
  uuid: string;
  email: string;
  status: number;
  category: number;
  company_text: string | null;
  created_at: string;
  invitation_sent_at: string | null;
  invitation_accepted_at: string | null;
  group_user?: {
    name: string;
    id: string;
  } | null;
  gate_subscription?: boolean;
  valquant_subscription?: boolean;
  nb_visits?: number;
  is_approved?: boolean;
};

// Definition of the columns

export const users_columns: ColumnDef<User>[] = [
  {
    accessorKey: "id",
    header: "Id",
  },
  {
    accessorKey: "group_user",
    header: "Groupe",
    cell: ({ row }) => {
      const group = row.original.group_user;
      return group ? (
        <ExternalLink
          href={toWorkspacePath(`/admin/group_users/${group.id}`)}
          className="text-xs lg:whitespace-nowrap"
          underline="hover"
        >
          {group.name}
        </ExternalLink>
      ) : (
        ""
      );
    },
  },
  {
    accessorKey: "email",
    header: "Email",
    cell: ({ row }) => row.getValue("email"),
  },
  {
    accessorKey: "status",
    header: "Statut",
    cell: ({ row }) => {
      // You may want to map status code to French label if available
      const status = row.getValue("status");
      return typeof status === "number" ? status.toString() : status;
    },
  },
  {
    accessorKey: "category",
    header: "Catégorie",
    cell: ({ row }) => {
      // You may want to map category code to French label if available
      const category = row.getValue("category");
      return typeof category === "number" ? category.toString() : category;
    },
  },
  {
    accessorKey: "company_text",
    header: "Société",
    cell: ({ row }) => row.getValue("company_text"),
  },
  {
    accessorKey: "created_at",
    header: "Créé le",
    cell: ({ row }) => parseDate(row.getValue("created_at")),
  },
  {
    accessorKey: "invitation_sent_at",
    header: "Invitation envoyée le",
    cell: ({ row }) =>
      row.getValue("invitation_sent_at")
        ? parseDate(row.getValue("invitation_sent_at"))
        : "",
  },
  {
    accessorKey: "invitation_accepted_at",
    header: "Invitation acceptée le",
    cell: ({ row }) =>
      row.getValue("invitation_accepted_at")
        ? parseDate(row.getValue("invitation_accepted_at"))
        : "",
  },
  {
    accessorKey: "gate_subscription",
    header: "Abonnement Gate",
    cell: ({ row }) =>
      row.original.gate_subscription !== undefined ? (
        row.original.gate_subscription ? (
          <CheckIcon className="text-green-600" />
        ) : null
      ) : null,
    enableHiding: true,
  },
  {
    accessorKey: "valquant_subscription",
    header: "Abonnement Valquant",
    cell: ({ row }) =>
      row.original.valquant_subscription !== undefined ? (
        row.original.valquant_subscription ? (
          <CheckIcon className="text-green-600" />
        ) : null
      ) : null,
    enableHiding: true,
  },
  {
    accessorKey: "nb_visits",
    header: "# visites",
    cell: ({ row }) => (
      <Link
        href={toWorkspacePath(
          `/admin/events?user_id=${row.original.uuid}&q[name_equals]=files%23show`,
        )}
        className="text-xs"
        underline="hover"
      >
        {formatNumber(row.getValue("nb_visits") ?? 0)}
      </Link>
    ),
  },
];
